import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import firebase from 'firebase/compat/app';
import { BehaviorSubject, Subject } from 'rxjs';
import { AuthService } from '../auth/auth.service';

@Injectable({
    providedIn: 'root'
})
export class NotificationService {

    unreadCount$ = new BehaviorSubject<number>(0);
    newNotification$ = new Subject<any>();
    private listenerUnsub: any = null;
    private isFirstSnapshot = true;

    constructor(
        private firestore: AngularFirestore,
        private auth: AuthService
    ) { }

    private get basePath() {
        return `accounts_abc/${this.auth.account_key}/notifications`;
    }

    initListener() {
        this.stopListener();

        if (!this.auth.account_key || !this.auth.location_key || !this.auth.area_key) return;

        this.isFirstSnapshot = true;

        this.listenerUnsub = this.firestore.firestore
            .collection(this.basePath)
            .where('target_location_key', '==', this.auth.location_key)
            .where('target_area_key', '==', this.auth.area_key)
            .where('status', '==', 'unread')
            .onSnapshot(snapshot => {
                this.unreadCount$.next(snapshot.size);

                if (this.isFirstSnapshot) {
                    this.isFirstSnapshot = false;
                    return;
                }

                snapshot.docChanges().forEach(change => {
                    if (change.type === 'added') {
                        const data = change.doc.data();
                        this.newNotification$.next({ $key: change.doc.id, ...data });
                    }
                });
            }, error => {
                console.error('Error listening to notifications:', error);
            });
    }

    stopListener() {
        if (this.listenerUnsub) {
            this.listenerUnsub();
            this.listenerUnsub = null;
        }
        this.isFirstSnapshot = true;
        this.unreadCount$.next(0);
    }

    async sendNotification(data: any) {
        const key = this.firestore.createId();
        const ref = this.firestore.firestore
            .collection(this.basePath)
            .doc(key);

        await ref.set({
            $key: key,
            type: data.type,
            status: data.status || 'unread',
            title: data.title,
            message: data.message,
            target_location_key: data.target_location_key,
            target_area_key: data.target_area_key,
            source_location_key: data.source_location_key,
            source_area_key: data.source_area_key || null,
            reference_key: data.reference_key,
            reference_type: data.reference_type,
            created_at: firebase.firestore.FieldValue.serverTimestamp(),
            created_by: this.auth.user_resume || null
        });

        return key;
    }

    async getNotifications(status?: string) {
        let query: any = this.firestore.firestore
            .collection(this.basePath)
            .where('target_location_key', '==', this.auth.location_key)
            .where('target_area_key', '==', this.auth.area_key)
            .orderBy('created_at', 'desc');

        if (status) {
            query = this.firestore.firestore
                .collection(this.basePath)
                .where('target_location_key', '==', this.auth.location_key)
                .where('target_area_key', '==', this.auth.area_key)
                .where('status', '==', status)
                .orderBy('created_at', 'desc');
        }

        const snapshot = await query.get();
        return snapshot.docs.map((doc: any) => ({ $key: doc.id, ...doc.data() }));
    }

    async markAsRead(notificationKey: string) {
        await this.firestore.firestore
            .collection(this.basePath)
            .doc(notificationKey)
            .update({ status: 'read' });
    }
}
